/// <reference path="./react-table-config.d.ts" />
import React, { ReactElement, useState } from 'react';
import { Column, PluginHook, TableState } from 'react-table';

import { MessageType } from '@savgroup-front-common/types';

import { Pagination } from '../../atoms/Pagination';
import DataTableSearchInput from '../../components/Datatable/DatatableFilters/DataTableSearchInput/DataTableSearchInput';
import { removeAccent } from '../../formatters';
import { useIsNewUiEnabled } from '../../hooks/useIsNewUiEnabled';

import TableBody from './Table.Body';
import TableFooter from './Table.Footer';
import TableHeader from './Table.Header';
import useMemoizedTable from './TableContent.hooks';
import {
  $PaginationContainer,
  $SearchContainer,
  $StyledTable,
  $StyledTBody,
  $StyledTFoot,
  $StyledTHead,
  $TableContainer,
} from './TableContent.styles';

interface TableContentProps<D extends Record<string, unknown>> {
  data: D[];
  columns: Column<D>[];
  plugins?: PluginHook<D>[];
  initialState?: Partial<TableState<D>>;
  dataTestId?: string;
  showSearch?: boolean;
  searchPlaceholder?: MessageType;
}

const TableContent = <D extends Record<string, unknown>>({
  columns,
  data,
  plugins = [],
  initialState,
  dataTestId,
  showSearch = false,
  searchPlaceholder,
}: TableContentProps<D>): ReactElement => {
  const [searchValue, setSearchValue] = useState<string>('');

  const filteredData = data.filter((item) => {
    return Object.values(item).some((val) => {
      if (val === null || val === undefined) {
        return false;
      }

      return removeAccent(val.toString().toLowerCase()).includes(
        removeAccent(searchValue.toLowerCase()),
      );
    });
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchValue(value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    hasFooter,
    hasPagination,
    gotoPage,
    pageCount,
    page,
    state,
  } = useMemoizedTable({ columns, data: filteredData, plugins, initialState });

  const { pageIndex } = state;

  const isNewUiEnabled = useIsNewUiEnabled();

  return (
    <>
      {showSearch && data.length > 0 && (
        <$SearchContainer>
          <DataTableSearchInput
            value={searchValue}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder={searchPlaceholder}
            dataTestId={`${dataTestId}_search`}
          />
        </$SearchContainer>
      )}

      <$TableContainer>
        <$StyledTable {...getTableProps()} data-testid={dataTestId}>
          <$StyledTHead>
            <TableHeader<D>
              groups={headerGroups}
              isNewUiEnabled={isNewUiEnabled}
            />
          </$StyledTHead>
          <$StyledTBody
            {...getTableBodyProps()}
            $isNewUiEnabled={isNewUiEnabled}
          >
            <TableBody<D>
              rows={hasPagination ? page : rows}
              prepareRow={prepareRow}
              dataTestId={dataTestId}
              isNewUiEnabled={isNewUiEnabled}
            />
          </$StyledTBody>
          {hasFooter && (
            <$StyledTFoot>
              <TableFooter<D> groups={footerGroups} dataTestId={dataTestId} />
            </$StyledTFoot>
          )}
        </$StyledTable>
      </$TableContainer>
      {hasPagination && pageCount > 1 && (
        <$PaginationContainer>
          <Pagination
            pagination={{
              page: pageIndex + 1,
              pageCount,
              pageSize: state.pageSize,
              total: data.length,
            }}
            onChangePage={(p) => gotoPage(p - 1)}
            paginationInterval={[15]}
          />
        </$PaginationContainer>
      )}
    </>
  );
};

export default TableContent;
